import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/Palette';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphone';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import { Chip, Grid, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Edit, SimpleForm, TextInput, useRecordContext } from 'react-admin';
import { ArtFilesProvider, useArtFiles } from './components/ArtFilesContext';
import ArtStatusHeader from './components/ArtStatusHeader';
import ArtToolbar from './components/ArtToolbar';
import FeatureIntro from '../../components/FeatureIntro';
import AssetGallery from './components/AssetGallery';
import ColorsCard from './components/ColorsCard';
import PlateCommandButton from './components/PlateCommandButton';
import ReadOnlyValue from './components/ReadOnlyValue';
import SectionCard from './components/SectionCard';
import { toBriefingPayload } from './lib/briefing';
import { AppArt, AppArtBucket, AppArtStatus } from './types';

const LOGO_ACCEPT = {
  'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp'],
  'application/pdf': ['.pdf'],
  'application/postscript': ['.ai', '.eps'],
};
// PDF também nas imagens do posto: material de agência costuma vir em PDF
// (arte da fachada, prancha do totem) e recusar isso obrigava a rede a
// converter antes de mandar. A página 1 é rasterizada no browser e vira o
// preview; o original continua guardado inteiro.
const PHOTO_ACCEPT = {
  'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.heic'],
  'application/pdf': ['.pdf'],
};
const MODEL_ACCEPT = { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] };
const PLATE_ACCEPT = { 'image/png': ['.png'] };

const ArtTitle = () => {
  const record = useRecordContext<AppArt>();
  return <span>Arte do aplicativo{record ? ` — ${record.chainName}` : ''}</span>;
};

/** Contagem viva do bucket, para o cabeçalho do card. */
const FileCountChip = ({ bucket, fallback }: { bucket: AppArtBucket; fallback: number }) => {
  const { buckets } = useArtFiles();
  const count = buckets[bucket]?.count ?? fallback;
  return <Chip size="small" variant="outlined" label={count === 1 ? '1 arquivo' : `${count} arquivos`} />;
};

const ArtForm = () => {
  const record = useRecordContext<AppArt>();
  const [palette, setPalette] = useState<string[]>([]);

  if (!record) { return null; }

  // Só o rascunho é editável. Depois de mandar para a geração o material não
  // pode mudar debaixo de quem está desenhando — para editar, volta a rascunho.
  const editable = record.status === AppArtStatus.draft;

  // Os modelos aparecem a partir do envio: é quando o dev tem o que subir.
  // Continuam editáveis enquanto ninguém confirmou a escolha. Rede marcada
  // como fora do fluxo nunca teve geração — o card só mostraria vazio.
  const showModels = ![AppArtStatus.draft, AppArtStatus.legacy].includes(record.status);
  const modelsEditable = [
    AppArtStatus.submitted, AppArtStatus.modelsReady, AppArtStatus.changesRequested,
  ].includes(record.status);

  const countOf = (bucket: AppArtBucket) =>
    (record.files || []).filter(file => file.bucket === bucket).length;

  return (
    <Grid container spacing={3} sx={{ width: '100%' }}>
      <ArtStatusHeader />

      {/* Briefing no topo: é o que o cliente pediu por escrito e o que decide
          como o resto é lido — a foto que ele quer, a cor que ele não quer.
          No fim da tela ele era escrito depois de tudo, e lido por ninguém. */}
      <SectionCard icon={<NotesOutlinedIcon color="primary" />} title="Briefing">
        {editable ? (
          <TextInput
            source="briefing"
            label={false}
            multiline
            minRows={3}
            fullWidth
            helperText="Vai junto para a geração dos modelos."
            placeholder="Ex.: o cliente quer puxar mais pro laranja; não usar a logo antiga; a fachada nova é a foto 2."
          />
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {record.briefing || '—'}
          </Typography>
        )}
      </SectionCard>

      <SectionCard
        icon={<ImageOutlinedIcon color="primary" />}
        title="Logos da rede"
        action={<FileCountChip bucket={AppArtBucket.logo} fallback={countOf(AppArtBucket.logo)} />}
      >
        <AssetGallery
          bucket={AppArtBucket.logo}
          hint="PDF, PNG, SVG ou AI. Se tiver versão horizontal e vertical, mande as duas. Clique para ver em tela cheia."
          accept={LOGO_ACCEPT}
          readOnly={!editable}
          showPrimary
          primaryLabel="Usar como logo principal"
          primaryActiveLabel="Logo principal"
        />
      </SectionCard>

      <SectionCard
        icon={<PhotoLibraryOutlinedIcon color="primary" />}
        title="Imagens do posto"
        action={<FileCountChip bucket={AppArtBucket.stationPhoto} fallback={countOf(AppArtBucket.stationPhoto)} />}
      >
        <AssetGallery
          bucket={AppArtBucket.stationPhoto}
          hint="Fachada, pista, loja de conveniência, totem de preço — JPG, PNG ou PDF. Quanto mais nítida, melhor o modelo."
          accept={PHOTO_ACCEPT}
          readOnly={!editable}
          onPaletteExtracted={setPalette}
        />
      </SectionCard>

      <SectionCard
        icon={<BadgeOutlinedIcon color="primary" />}
        title="Identidade"
        size={{ xs: 12, md: 6 }}
      >
        {editable ? (
          <>
            <TextInput source="appName" label="Nome do posto" variant="standard" fullWidth />
            <TextInput source="slogan" label="Slogan" variant="standard" fullWidth />
          </>
        ) : (
          <Grid container spacing={2}>
            <ReadOnlyValue label="Nome do posto" value={record.appName} />
            <ReadOnlyValue label="Slogan" value={record.slogan} />
          </Grid>
        )}
      </SectionCard>

      <SectionCard
        icon={<PaletteOutlinedIcon color="primary" />}
        title="Cores"
        size={{ xs: 12, md: 6 }}
      >
        <ColorsCard readOnly={!editable} palette={palette} record={record} />
      </SectionCard>

      {showModels && (
        <SectionCard
          icon={<PhoneIphoneOutlinedIcon color="primary" />}
          title="Modelos gerados"
          action={<FileCountChip bucket={AppArtBucket.model} fallback={countOf(AppArtBucket.model)} />}
        >
          <AssetGallery
            bucket={AppArtBucket.model}
            hint="Cada modelo é a arte dentro do app — boas-vindas e home, com os botões e o saldo que o aplicativo desenha por cima. Clique para ver em tela cheia e use “Escolher este modelo” no que o cliente aprovou."
            accept={MODEL_ACCEPT}
            readOnly={!modelsEditable}
            showPrimary
            primaryLabel="Escolher este modelo"
            primaryActiveLabel="Modelo escolhido"
            tall
            showMockup
            showAdjust
          />
        </SectionCard>
      )}

      {/* A placa fica FORA do `showModels`.

          Ela não é arte do aplicativo: não entra no build e não participa do
          fluxo de aprovação, então prender a seção ao estado da arte esconderia
          a placa de uma rede que ainda está em rascunho — e a placa não depende
          da arte do app para existir.

          Pelo mesmo motivo a galeria não recebe o `readOnly` do rascunho: o
          gerador sobe placa em qualquer status, e com a trava a placa de uma
          rede já enviada aparecia sem dar para remover nem marcar a vigente. */}
      <SectionCard
        icon={<QrCode2OutlinedIcon color="primary" />}
        title="Placas de posto"
        action={(
          <Stack direction="row" spacing={1} alignItems="center">
            <FeatureIntro
              id="app-art-plate-command"
              title="Gerar a placa de posto desta rede"
              description="Como nos modelos, o gerador roda na sua máquina. Aqui você monta o comando com o id da rede e cola no terminal; a placa sobe para esta seção com os espaços do QR e do código vazios."
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <PlateCommandButton />
            </FeatureIntro>
            <FileCountChip bucket={AppArtBucket.stationPlate} fallback={countOf(AppArtBucket.stationPlate)} />
          </Stack>
        )}
      >
        <AssetGallery
          bucket={AppArtBucket.stationPlate}
          hint="A peça quadrada que fica na bomba, com o QR e o código de abastecimento. Ela sai do gerador com os dois espaços VAZIOS — o QR e o código são compostos por cima, nas coordenadas que vêm junto do arquivo. Marque com ★ a placa vigente da rede."
          accept={PLATE_ACCEPT}
          showPrimary
          primaryLabel="Usar como placa vigente"
          primaryActiveLabel="Placa vigente"
          showPlate
        />
      </SectionCard>
    </Grid>
  );
};

const AppArtEdit = () => (
  <Edit
    title={<ArtTitle />}
    // pessimistic: o upload dos arquivos já bateu no servidor, então um
    // rollback otimista do briefing deixaria a tela contando duas histórias.
    mutationMode="pessimistic"
    transform={toBriefingPayload}
    redirect={false}
    actions={false}
  >
    <ArtFilesProvider>
      <SimpleForm toolbar={<ArtToolbar />}>
        <ArtForm />
      </SimpleForm>
    </ArtFilesProvider>
  </Edit>
);

export default AppArtEdit;
